import React, { useEffect, useRef } from 'react';
import '../../styles/Objects.css';

interface DriftProps {
  children: React.ReactNode;
  className?: string;
  /** furthest the object eases toward the cursor, in px */
  maxShift?: number;
}

/**
 * Ambient cursor drift: the wrapped object lerps a few tens of pixels toward
 * the pointer, harder when the cursor is near. Fine-pointer desktop only;
 * inert (pointer-events: none) and skipped under reduced motion.
 */
const Drift: React.FC<DriftProps> = ({ children, className = '', maxShift = 28 }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      !window.matchMedia('(pointer: fine) and (min-width: 1100px)').matches ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    let raf = 0;
    let targetX = 0, targetY = 0, x = 0, y = 0;

    const tick = () => {
      x += (targetX - x) * 0.06;
      y += (targetY - y) * 0.06;
      el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
      raf = Math.abs(targetX - x) + Math.abs(targetY - y) > 0.1 ? requestAnimationFrame(tick) : 0;
    };
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      const dist = Math.hypot(dx, dy) || 1;
      const pull = Math.min(1, 480 / dist);
      targetX = (dx / dist) * maxShift * pull;
      targetY = (dy / dist) * maxShift * pull;
      if (!raf) raf = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [maxShift]);

  return (
    <div ref={ref} className={`obj-drift ${className}`} aria-hidden="true">
      {children}
    </div>
  );
};

export default Drift;
